import { ComponentClass, forwardRef } from 'react';
import styled from '@emotion/styled';
import {
  StyledFormGroup,
  StyledInput,
  StyledSubTitle,
} from '@selfserve/react-ui';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormValues } from '@selfserve/react-hooks';

const {
  CardComponent,
  CardNumber,
  CardExpiry,
  CardCVV,
}: // eslint-disable-next-line @typescript-eslint/no-var-requires
any = require('@chargebee/chargebee-js-react-wrapper');

const applyCommonInputStyled = (
  Component: ComponentClass<{
    id?: string;
    className?: string;
    placeholder?: string;
    onChange?: (value: any) => void;
    onBlur?: (value: any) => void;
  }>
) =>
  styled(Component)(({ theme, error }: { theme?: any; error?: boolean }) => ({
    border: `1px solid ${theme.palette.grey['400']}`,
    ...(error && { borderColor: theme.palette.danger.main }),
    borderRadius: '4px',
    padding: '9px 8px',
    ...(!error && {
      '&.focused': {
        borderColor: theme.palette.primary.main,
      },
    }),
  }));

const StyledCardNumber = applyCommonInputStyled(CardNumber);

const StyledCardExpiry = applyCommonInputStyled(CardExpiry);

const StyledCardCVV = applyCommonInputStyled(CardCVV);

interface Props {
  formik: FormikProps<FormValues>;
  handleDropdownChange: (name: string) => (value: string) => void;
  handleCardInputsChange: (field: any) => void;
  handleCardInputsBlur: (field: any) => void;
  reverse?: boolean;
}

export const CheckoutPaymentForm = forwardRef(
  (
    {
      handleCardInputsChange,
      handleCardInputsBlur,
      formik: { handleChange, handleBlur, values, touched, errors },
      reverse,
    }: Props,
    ref
  ) => {
    const { t } = useTranslation();

    return (
      <div>
        <StyledSubTitle>{t('common.payment-details')}</StyledSubTitle>
        <CardComponent
          className="field"
          classes={{ focus: 'focused' }}
          ref={ref}
        >
          <StyledFormGroup>
            <label htmlFor="cardnumber">{t('common.card-number')}</label>
            <StyledCardNumber
              id="cardnumber"
              onChange={handleCardInputsChange}
              onBlur={handleCardInputsBlur}
              error={touched.number && !!errors.number}
              placeholder=" "
            />
            {touched.number && errors.number && <p>{errors.number}</p>}
          </StyledFormGroup>
          <StyledFormGroup fullWidth row>
            <StyledFormGroup withoutMargin fullWidth minWidth={110}>
              <label htmlFor="exp-date">{t('common.expiration-date')}</label>
              <StyledCardExpiry
                id="exp-date"
                onChange={handleCardInputsChange}
                onBlur={handleCardInputsBlur}
                error={touched.expiry && !!errors.expiry}
                placeholder=" "
              />
              {touched.expiry && errors.expiry && <p>{errors.expiry}</p>}
            </StyledFormGroup>
            <StyledFormGroup withoutMargin fullWidth minWidth={110}>
              <label htmlFor="cvc">{t('common.cvv')}</label>
              <StyledCardCVV
                id="cvc"
                onChange={handleCardInputsChange}
                onBlur={handleCardInputsBlur}
                error={touched.cvv && !!errors.cvv}
                placeholder=" "
              />
              {touched.cvv && errors.cvv && <p>{errors.cvv}</p>}
            </StyledFormGroup>
          </StyledFormGroup>
        </CardComponent>
        <StyledFormGroup withoutMargin fullWidth row>
          <StyledFormGroup withoutMargin={!reverse}>
            <StyledInput
              id="firstName"
              name="firstName"
              inputLabel={t('common.first-name')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
              error={touched.firstName ? !!errors.firstName : false}
              inputProps={{ 'aria-label': t('aria.first-name') }}
              fullWidth
            />
            {touched.firstName && errors.firstName && (
              <p className="p-error">{errors.firstName}</p>
            )}
          </StyledFormGroup>
          <StyledFormGroup withoutMargin={!reverse}>
            <StyledInput
              id="lastName"
              name="lastName"
              inputLabel={t('common.last-name')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              error={touched.lastName ? !!errors.lastName : false}
              inputProps={{ 'aria-label': t('aria.last-name') }}
              fullWidth
            />
            {touched.lastName && errors.lastName && (
              <p className="p-error">{errors.lastName}</p>
            )}
          </StyledFormGroup>
        </StyledFormGroup>
      </div>
    );
  }
);
